import { useEffect, useState } from "react"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { ScrollArea } from "@/components/ui/scroll-area"
import {
  FileText,
  CalendarClock,
  User,
  Stethoscope,
  Paperclip,
  MapPin,
  Download,
  Loader2,
} from "lucide-react"
import { format } from "date-fns"
import { Button } from "@/components/ui/button"
import { toast } from "@/components/ui/use-toast"
import { FileViewer } from "@/components/FileViewer"

const getStatusBadge = (status) => {
  const badges = {
    PENDING: "bg-yellow-100 text-yellow-800",
    APPROVED: "bg-green-100 text-green-800",
    REJECTED: "bg-red-100 text-red-800",
    COMPLETED: "bg-blue-100 text-blue-800",
  }
  const labels = {
    PENDING: "Pending",
    APPROVED: "Approved",
    REJECTED: "Rejected",
    COMPLETED: "Completed",
  }
  return (
    <span
      className={`px-2 py-1 rounded-full text-xs font-medium ${badges[status]}`}
    >
      {labels[status]}
    </span>
  )
}

const isImageFile = (fileName) => {
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp']
  return imageExtensions.some(ext => 
    fileName.toLowerCase().endsWith(ext)
  )
}

const isPdfFile = (fileName) => {
  return fileName.toLowerCase().endsWith('.pdf')
}

export function AppointmentDetailsSheet({ appointment, open, onOpenChange }) {
  const formatDateTime = (dateString) => {
    const date = new Date(dateString)
    return format(date, "PPP 'at' p")
  }

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-[400px] sm:w-[540px] overflow-y-auto">
          <SheetHeader className="mb-6">
            <SheetTitle>Appointment Details</SheetTitle>
          </SheetHeader>
          <ScrollArea className="h-[calc(100vh-100px)] pr-4">
            <div className="space-y-6">
              {/* Status */}
              <div className="flex items-center space-x-2">
                <h3 className="font-medium">Status:</h3>
                {getStatusBadge(appointment.appointment_status)}
              </div>

              {/* Patient & Doctor Info */}
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <User className="h-5 w-5 text-blue-500 mt-1" />
                  <div>
                    <h3 className="font-medium">Patient</h3>
                    <p className="text-sm text-gray-600">
                      {appointment.patient_name}
                    </p>
                    <p className="text-sm text-gray-500">
                      {appointment.mobile_number}
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Stethoscope className="h-5 w-5 text-blue-500 mt-1" />
                  <div>
                    <h3 className="font-medium">Doctor</h3>
                    <p className="text-sm text-gray-600">
                      {appointment.doctor_name}
                    </p>
                  </div>
                </div>
                {appointment.location && (
                  <div className="flex items-start space-x-3">
                    <MapPin className="h-5 w-5 text-blue-500 mt-1" />
                    <div>
                      <h3 className="font-medium">Location</h3>
                      <p className="text-sm text-gray-600">{appointment.location}</p>
                    </div>
                  </div>
                )}
                <div className="flex items-start space-x-3">
                  <CalendarClock className="h-5 w-5 text-blue-500 mt-1" />
                  <div>
                    <h3 className="font-medium">Appointment Time</h3>
                    <p className="text-sm text-gray-600">
                      {formatDateTime(appointment.appointment_date)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Symptoms & Reason */}
              <div className="space-y-4 border-t pt-4">
                <div>
                  <h3 className="font-medium mb-2">Symptoms</h3>
                  <p className="text-sm text-gray-600">{appointment.symptoms}</p>
                </div>
                <div>
                  <h3 className="font-medium mb-2">Reason</h3>
                  <p className="text-sm text-gray-600">{appointment.reason}</p>
                </div>
              </div>

              {/* Additional Notes */}
              {appointment.addition_notes && (
                <div className="space-y-2 border-t pt-4">
                  <div className="flex items-center space-x-2">
                    <FileText className="h-5 w-5 text-blue-500" />
                    <h3 className="font-medium">Additional Notes</h3>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600 whitespace-pre-wrap">
                      {appointment.addition_notes}
                    </p>
                  </div>
                </div>
              )}

              {/* Attached File */}
              <div className="border-t pt-4">
                <h3 className="font-medium mb-3">Attached File</h3>
                {appointment?.file_path ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 bg-gray-50 p-3 rounded-lg">
                      <Paperclip className="h-4 w-4 text-blue-500" />
                      <span className="text-sm text-gray-600 flex-1 truncate">
                        {appointment.file_name}
                      </span>
                    </div>
                    <FileViewer
                      filePath={appointment.file_path}
                      fileType={appointment.file_type}
                      fileName={appointment.file_name}
                      previewClassName="max-w-[90vw] max-h-[80vh]"
                      dialogClassName="max-w-5xl"
                    >
                      <Button 
                        variant="outline" 
                        className="w-full text-blue-600 hover:text-blue-800"
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        View Image
                      </Button>
                    </FileViewer>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 bg-gray-50 p-3 rounded-lg">
                    <span className="text-sm text-gray-500">
                      No file has been attached
                    </span>
                  </div>
                )}
              </div>
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>
    </>
  )
}
